import { ReactNode, createContext, useState, useMemo } from 'react';
// hooks
import { getValidDate } from 'src/utils/dateTime';

// ----------------------------------------------------------------------

type DateCell = Date | string | null;

type DateRange = { from: DateCell; to: DateCell };

export type DateFilterContextProps = {
  handleChange: (newValue: DateRange) => void;
  dateRange: DateRange;
  localDateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  setLocalDateRange: (value: DateRange) => void;
  dateFrom: string;
  dateTo: string;
  cumulativeDateFrom: string;
  cumulativeDateTo: string;
  setUpdateTableData: (v: any) => void;
};

const DateFilterContext = createContext<DateFilterContextProps>({
  handleChange: () => {},
  dateRange: {
    from: null,
    to: null,
  },
  localDateRange: { from: null, to: null },
  setDateRange: (value: DateRange) => {},
  setLocalDateRange: (value: DateRange) => {},
  dateFrom: '',
  dateTo: '',
  cumulativeDateFrom: '',
  cumulativeDateTo: '',
  setUpdateTableData: () => {}
});

type DateFilterProviderProps = {
  children: ReactNode;
};

const addMonths = (input: any, months: any) => {
  const date = new Date(input);
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  return date;
};

const modifyDateString = (date: DateCell) => {
  if (!date) {
    return '';
  }

  const d = new Date(date);
  const normalizeDateShift = new Date(d.getTime() + Math.abs(d.getTimezoneOffset() * 60000));

  return normalizeDateShift.toISOString();
};

function DateFilterProvider({ children }: DateFilterProviderProps) {
  const today = new Date(new Date().setHours(0,0,0,0));
  const sixMonthsAgo = addMonths(today, -6);
  const [updateTableData, setUpdateTableData] = useState<{ func: () => void }>({ func: () => {} });

  const [dateRange, setDateRange] = useState<DateRange>({ from: sixMonthsAgo, to: today });
  const [localDateRange, setLocalDateRange] = useState<DateRange>({
    from: dateRange.from,
    to: dateRange.to,
  });

  const handleChange = (newValue: DateRange) => {
    if (newValue) {
      setDateRange({ from: getValidDate(newValue.from), to: getValidDate(newValue.to) });
      updateTableData && updateTableData.func();
    }
  };

  const dateFrom = dateRange.from ? modifyDateString(dateRange.from) : '';
  const dateTo = dateRange.to ? modifyDateString(dateRange.to) : '';

  const cumulativeDateFrom = modifyDateString('01/01/1970');

  const cumulativeDateTo = useMemo(() => {
    if (!dateFrom) {
      return '';
    }

    let date = new Date(dateFrom);
    let n = new Date(date.setDate(date.getDate() - 1));
    n.setDate(date.getDate() - 1);

    return modifyDateString(date);
  }, [dateFrom]);

  return (
    <DateFilterContext.Provider
      value={{
        dateRange,
        handleChange,
        setDateRange,
        dateFrom,
        dateTo,
        cumulativeDateFrom,
        cumulativeDateTo,
        setUpdateTableData,
        localDateRange,
        setLocalDateRange,
      }}
    >
      {children}
    </DateFilterContext.Provider>
  );
}

export { DateFilterProvider, DateFilterContext };
